import { useState, useEffect, useRef, useMemo } from 'react';
import { formatPhone } from '../../../../libs.jsx';

/* Emergency phones read (XXX) XXX-XXXX here, as in V1's System Settings. */
const formatEmergencyPhone = (digits) => formatPhone(digits, 'paren');

/** Strip all non-digits */
const toDigits = (str) => (str || '').replace(/\D/g, '');

/** Email validation regex */
const isValidEmail = (email) => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);

const CloudServerSettingsTab = ({
    systemOfflineTime: offlineTimeProp,
    emergencyEmails: emailsProp,
    emergencyPhones: phonesProp,
    onSave,
    saving,
}) => {
    // Parse comma strings into arrays
    const initialEmails = useMemo(() => {
        return emailsProp ? emailsProp.split(',').map((s) => s.trim()).filter(Boolean) : [];
    }, [emailsProp]);

    const initialPhones = useMemo(() => {
        return phonesProp
            ? phonesProp.split(',').map((s) => formatEmergencyPhone(toDigits(s.trim()))).filter(Boolean)
            : [];
    }, [phonesProp]);

    const [offlineTime, setOfflineTime] = useState(offlineTimeProp || 15);
    const [emailTags, setEmailTags] = useState([]);
    const [phoneTags, setPhoneTags] = useState([]);
    const [emailInput, setEmailInput] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const emailRef = useRef(null);
    const phoneRef = useRef(null);

    // Sync from props
    useEffect(() => {
        setOfflineTime(offlineTimeProp || 15);
        setEmailTags([...initialEmails]);
        setPhoneTags([...initialPhones]);
        setEmailInput('');
        setPhoneInput('');
        setEmailError('');
        setPhoneError('');
    }, [offlineTimeProp, initialEmails, initialPhones]);

    // Dirty detection — auto-resets when values match originals
    const isDirty = useMemo(() => {
        return (
            offlineTime !== (offlineTimeProp || 15) ||
            JSON.stringify(emailTags) !== JSON.stringify(initialEmails) ||
            JSON.stringify(phoneTags) !== JSON.stringify(initialPhones)
        );
    }, [offlineTime, emailTags, phoneTags, offlineTimeProp, initialEmails, initialPhones]);

    // --- Email tag handlers ---
    const addEmail = (raw) => {
        const email = raw.trim();
        if (!email) return;
        if (!isValidEmail(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        if (emailTags.some((t) => t.toLowerCase() === email.toLowerCase())) {
            setEmailError('This email is already in the list.');
            return;
        }
        setEmailTags((prev) => [...prev, email]);
        setEmailInput('');
        setEmailError('');
    };

    const removeEmail = (idx) => {
        setEmailTags((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleEmailKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addEmail(emailInput);
        } else if (e.key === 'Backspace' && !emailInput && emailTags.length > 0) {
            removeEmail(emailTags.length - 1);
        }
    };

    // --- Phone tag handlers ---
    const addPhone = (raw) => {
        const digits = toDigits(raw);
        if (!digits) return;
        if (digits.length !== 10) {
            setPhoneError('Phone number must be exactly 10 digits.');
            return;
        }
        const formatted = formatEmergencyPhone(digits);
        if (phoneTags.includes(formatted)) {
            setPhoneError('This phone number is already in the list.');
            return;
        }
        setPhoneTags((prev) => [...prev, formatted]);
        setPhoneInput('');
        setPhoneError('');
    };

    const removePhone = (idx) => {
        setPhoneTags((prev) => prev.filter((_, i) => i !== idx));
    };

    const handlePhoneKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addPhone(phoneInput);
        } else if (e.key === 'Backspace' && !phoneInput && phoneTags.length > 0) {
            removePhone(phoneTags.length - 1);
        }
    };

    const handleSave = () => {
        const rawPhones = phoneTags.map((p) => toDigits(p));
        onSave({
            emergency_email: emailTags.join(','),
            emergency_text: rawPhones.join(','),
            system_offline_time: offlineTime,
        });
    };

    const handleCancel = () => {
        setOfflineTime(offlineTimeProp || 15);
        setEmailTags([...initialEmails]);
        setPhoneTags([...initialPhones]);
        setEmailInput('');
        setPhoneInput('');
        setEmailError('');
        setPhoneError('');
    };

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <div>
                    <h2 className="pp-section__title">Server Settings</h2>
                    <p className="ss-section-desc">
                        Configure system offline detection time and emergency notification recipients.
                    </p>
                </div>
                {isDirty ? (
                    <div className="pp-section__actions">
                        <button className="dp-btn dp-btn--cancel" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="pp-section__body">
                {/* System Offline Time */}
                <div className="ss-field" style={{ maxWidth: '280px', marginBottom: 'var(--space-6)' }}>
                    <label className="ss-field__label">System Offline Time (minutes)</label>
                    <input
                        className="ss-number-input"
                        type="number"
                        min={10}
                        max={60}
                        step={1}
                        value={offlineTime}
                        onChange={(e) => {
                            let v = parseInt(e.target.value);
                            if (isNaN(v)) v = 15;
                            setOfflineTime(Math.max(10, Math.min(60, v)));
                        }}
                    />
                    <p className="ss-field__hint">Range: 10 – 60 minutes</p>
                </div>

                {/* Emergency Email Tags */}
                <div className="ss-field" style={{ marginBottom: 'var(--space-6)' }}>
                    <label className="ss-field__label">Emergency Notification Emails</label>
                    <div
                        className={`ss-tag-input${emailError ? ' is-invalid' : ''}`}
                        onClick={() => emailRef.current?.focus()}
                    >
                        {emailTags.map((tag, i) => (
                            <span key={`${tag}-${i}`} className="ss-tag-input__tag">
                                {tag}
                                <button
                                    className="ss-tag-input__remove"
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); removeEmail(i); }}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        <input
                            ref={emailRef}
                            className="ss-tag-input__input"
                            type="email"
                            placeholder={emailTags.length === 0 ? 'Enter email and press Enter' : ''}
                            value={emailInput}
                            onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                            onKeyDown={handleEmailKeyDown}
                            onBlur={() => { if (emailInput.trim()) addEmail(emailInput); }}
                        />
                    </div>
                    {emailError ? <p className="ss-tag-input__error">{emailError}</p> : null}
                    <p className="ss-tag-input__hint">Press Enter or comma to add an email address.</p>
                </div>

                {/* Emergency Phone Tags */}
                <div className="ss-field">
                    <label className="ss-field__label">Emergency Notification Phones</label>
                    <div
                        className={`ss-tag-input${phoneError ? ' is-invalid' : ''}`}
                        onClick={() => phoneRef.current?.focus()}
                    >
                        {phoneTags.map((tag, i) => (
                            <span key={`${tag}-${i}`} className="ss-tag-input__tag">
                                {tag}
                                <button
                                    className="ss-tag-input__remove"
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); removePhone(i); }}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        <input
                            ref={phoneRef}
                            className="ss-tag-input__input"
                            type="tel"
                            placeholder={phoneTags.length === 0 ? 'Enter 10-digit phone and press Enter' : ''}
                            value={phoneInput}
                            onChange={(e) => { setPhoneInput(e.target.value); setPhoneError(''); }}
                            onKeyDown={handlePhoneKeyDown}
                            onBlur={() => { if (phoneInput.trim()) addPhone(phoneInput); }}
                        />
                    </div>
                    {phoneError ? <p className="ss-tag-input__error">{phoneError}</p> : null}
                    <p className="ss-tag-input__hint">Press Enter or comma to add. Numbers will auto-format to (XXX) XXX-XXXX.</p>
                </div>
            </div>
        </div>
    );
};

export default CloudServerSettingsTab;
